import { createSlice } from '@reduxjs/toolkit';
import { SubItemsState } from './types';
import { fetchProductSubitems } from './thunk';
import type { RootState } from '@/redux/store';

const initialState: SubItemsState = {
  items: [],
  loading: false,
  error: null,
  lastItemId: null,
};

const normalizeId = (id: unknown): string | null =>
  id == null ? null : String(id);

const subitemsSlice = createSlice({
  name: 'products/subitems',
  initialState,
  reducers: {
    resetSubitems(state) {
      state.items = [];
      state.loading = false;
      state.error = null;
      state.lastItemId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductSubitems.pending, (state, action) => {
        const itemId = action.meta.arg?.item_id ?? null;

        if (normalizeId(itemId) !== normalizeId(state.lastItemId)) {
          state.items = [];
        }

        state.loading = true;
        state.error = null;
        state.lastItemId = itemId;
      })
      .addCase(fetchProductSubitems.fulfilled, (state, action) => {
        if (
          normalizeId(action.meta.arg?.item_id) !==
          normalizeId(state.lastItemId)
        ) {
          return;
        }

        state.loading = false;
        state.items = action.payload ?? [];
      })
      .addCase(fetchProductSubitems.rejected, (state, action) => {
        if (
          normalizeId(action.meta.arg?.item_id) !==
          normalizeId(state.lastItemId)
        ) {
          return;
        }

        state.loading = false;
        state.error = action.payload || 'Failed to load subitems';
      });
  },
});

export const { resetSubitems } = subitemsSlice.actions;
export default subitemsSlice.reducer;

export const selectSubitems = (s: RootState) => s.productsSubitems.items;
export const selectSubitemsLoading = (s: RootState) =>
  s.productsSubitems.loading;
export const selectSubitemsError = (s: RootState) => s.productsSubitems.error;
export const selectLastQueriedItemId = (s: RootState) =>
  s.productsSubitems.lastItemId;
